<template>
  <div
    ref="transitionContainer"
    class="transition-container"
    :class="{ 'overflow-clip': isAnimating }"
    :style="{ transitionDuration: `${durationMs}ms` }"
  >
    <div ref="innerContainer" class="inner-container">
      <Transition name="height-fade">
        <slot />
      </Transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useResizeObserver, useTimeoutFn } from '@vueuse/core';
import { ref } from 'vue';

const props = withDefaults(defineProps<{ durationMs?: number }>(), { durationMs: 600 });

const transitionContainer = ref<HTMLDivElement>();
const innerContainer = ref<HTMLDivElement>();

// While growing, the content is taller than the container; clipping only during the animation
// keeps it from spilling without permanently cutting off focus rings. It has to be `clip`
// rather than `hidden`: a hidden box can still be scrolled, and anything focusing itself mid
// animation (a Tiptap editor, say) would scroll the container instead of the page.
const isAnimating = ref(false);
const { start: stopClippingLater } = useTimeoutFn(
  () => {
    isAnimating.value = false;
  },
  () => props.durationMs + 50,
  { immediate: false },
);

const handleEnter = (el: HTMLElement) => {
  if (!transitionContainer.value) return;
  const targetHeight = `${Math.round(el.getBoundingClientRect().height * 10) / 10}px`;
  if (transitionContainer.value.style.height === targetHeight) return;

  if (transitionContainer.value.style.height) {
    // Retarget in place: the browser picks up the currently animated height as the start.
    isAnimating.value = true;
    stopClippingLater();
    transitionContainer.value.style.height = targetHeight;
    return;
  }
  // First measurement (nothing to animate yet): pin the height so later changes can transition.
  const currentHeight = transitionContainer.value.getBoundingClientRect().height;
  transitionContainer.value.style.height = `${currentHeight}px`;
  window.requestAnimationFrame(() => {
    if (!transitionContainer.value) return;
    transitionContainer.value.style.height = targetHeight;
  });
};

useResizeObserver(innerContainer, () => {
  if (innerContainer.value) {
    handleEnter(innerContainer.value);
  }
});
</script>

<style scoped>
.transition-container {
  /* ease-out keeps a nested container close behind content that animates its own height. */
  transition: height 0.6s ease-out;
}

.inner-container {
  display: grid;
  position: relative;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
}

.inner-container > :deep(*) {
  grid-column: 1;
  grid-row: 1;
  justify-self: stretch;
  align-self: start;
}

.height-fade-enter-active,
.height-fade-leave-active {
  transition: opacity 0.3s;
}

.height-fade-enter-active {
  transition-delay: 0.3s;
}

.height-fade-leave-to {
  position: absolute;
  width: 100%;
}

.height-fade-enter-from,
.height-fade-leave-to {
  opacity: 0;
}
</style>
